import { useId } from 'react';
import type { ReactNode } from 'react';
import { Alert, Box, Card, CardContent, Chip, CircularProgress, Stack, Typography } from '@mui/material';

export interface BaseChartProps {
  title?: string;
  subtitle?: string;
  height?: number;
  loading?: boolean;
  error?: string;
  badge?: string;
}

export const ChartFrame = ({
  title,
  subtitle,
  height = 300,
  loading,
  error,
  badge,
  hasData,
  children,
}: BaseChartProps & { hasData: boolean; children: ReactNode }) => {
  const titleId = useId();
  return (
    <Card component="section" aria-labelledby={title ? titleId : undefined} sx={{ height: '100%', minWidth: 0 }}>
      <CardContent>
        <Stack spacing={2}>
          {title && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography component="h2" variant="h6" id={titleId}>
                {title}
              </Typography>
              {badge && <Chip label={badge} size="small" variant="outlined" />}
            </Stack>
          )}
          {subtitle && <Typography color="text.secondary">{subtitle}</Typography>}
          {loading ? (
            <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ minHeight: height }}>
              <CircularProgress aria-label={`Loading ${title || 'chart'}`} />
              <Typography>Loading chart data…</Typography>
            </Stack>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : !hasData ? (
            <Typography color="text.secondary" sx={{ py: 4 }}>
              No data available
            </Typography>
          ) : (
            <Box sx={{ width: '100%', minWidth: 0, height }}>{children}</Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
